'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  defaultDiscoverParams,
  parseDiscoverParams,
  serializeDiscoverParams,
  type DiscoverParams,
} from '@/lib/discover/url-state';

/**
 * Client hook that exposes the canonical `/discover` URL state plus mutators
 * that write back through `router.push` (no scroll, no full navigation).
 *
 * Filter and view hops stack history so browser Back undoes them (WEB-769 /
 * WEB-691 AC4): close-map → Back returns to `view=map` with filters still
 * set; another Back returns to the filtered list. `replaceParams` replaces
 * the param *object* (e.g. "Clear all") and still pushes unless the caller
 * passes `{ history: 'replace' }` (4xx revert in `onDiscoverError`).
 *
 * - `params`: the current parsed `DiscoverParams` derived from `useSearchParams`.
 * - `setParams`: shallow merge of partial updates onto the current `params`.
 * - `replaceParams`: replace the entire param set (use for "Clear all" or full
 *   resets).
 *
 * The pure parse/serialize live in `lib/discover/url-state.ts` and are reused
 * by the SSR page so server and client agree on the canonical shape.
 * Map viewport pans that must not stack belong in `useMapUrlState` (`replace`).
 */
export interface DiscoverUrlWriteOptions {
  /**
   * History strategy for this write. Default `push` so Back undoes the hop
   * (WEB-769). Use `replace` only when the current entry must be overwritten
   * — today that is the 4xx filter revert in `onDiscoverError`, so Back
   * cannot restore a rejected filter.
   */
  history?: 'push' | 'replace';
}

export interface UseDiscoverParamsResult {
  params: DiscoverParams;
  setParams: (updates: Partial<DiscoverParams>, options?: DiscoverUrlWriteOptions) => void;
  replaceParams: (next: DiscoverParams, options?: DiscoverUrlWriteOptions) => void;
}

export function useDiscoverParams(): UseDiscoverParamsResult {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const params = useMemo(
    () => parseDiscoverParams(new URLSearchParams(searchParams.toString())),
    [searchParams],
  );

  // Tracks the most recently written params so sequential setParams calls in
  // a single event loop merge against each other instead of a stale snapshot.
  const latestParamsRef = useRef<DiscoverParams>(params);
  useEffect(() => {
    latestParamsRef.current = params;
  }, [params]);

  // WEB-1282 — canonicalize the address bar's `date`. A wire-keyword alias
  // (`?date=today`) parses to its preset and an unknown value (`?date=xyz`)
  // parses to "no date filter"; either way the raw value would mislabel the
  // view, so rewrite ONLY `date` (to `params.date`, or remove it) and drop the
  // legacy `when`. Every other key is left as the URL has it.
  //
  // Native `history.replaceState`, not `router.replace`: the parsed params do
  // not change, so there is nothing to re-render on the server. Next patches
  // `replaceState` to update `useSearchParams` with no RSC request, and no
  // history entry is added, so Back never lands on the stale link. Every
  // Discover molecule mounts this hook; checking the live location turns the
  // rest into no-ops once the first instance has written. After the write
  // the raw value equals `params.date`, so the effect settles without looping.
  const rawQuery = searchParams.toString();
  useEffect(() => {
    const current = new URLSearchParams(rawQuery);
    const raw = current.get('date') ?? current.get('when');
    if (raw === null || raw === (params.date ?? null)) return;

    if (params.date) current.set('date', params.date);
    else current.delete('date');
    current.delete('when');
    const query = current.toString();
    const { pathname: livePath, search, hash } = window.location;
    const href = `${livePath}${query.length > 0 ? `?${query}` : ''}${hash}`;
    if (href === `${livePath}${search}${hash}`) return;
    window.history.replaceState(null, '', href);
  }, [rawQuery, params.date]);

  const writeToUrl = useCallback(
    (next: DiscoverParams, options?: DiscoverUrlWriteOptions) => {
      const serialized = serializeDiscoverParams(next).toString();
      const href = serialized.length > 0 ? `${pathname}?${serialized}` : pathname;
      const navigate = options?.history === 'replace' ? router.replace : router.push;
      navigate(href, { scroll: false });
    },
    [router, pathname],
  );

  const setParams = useCallback(
    (updates: Partial<DiscoverParams>, options?: DiscoverUrlWriteOptions) => {
      const next = { ...latestParamsRef.current, ...updates };
      latestParamsRef.current = next;
      writeToUrl(next, options);
    },
    [writeToUrl],
  );

  const replaceParams = useCallback(
    (next: DiscoverParams, options?: DiscoverUrlWriteOptions) => {
      latestParamsRef.current = next;
      writeToUrl(next, options);
    },
    [writeToUrl],
  );

  return { params, setParams, replaceParams };
}

export { defaultDiscoverParams };
export type { DiscoverParams };
